import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { ListTableComponent } from '@ppl/ui';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { NotificationService } from '../../../../shared/services/notification.service';
import { UserService } from '../../../../services/user.service';
import { correctionResults, PayrollCorrectionService } from '../../../../services/payroll-correction.service';
import { CorrectionLineFilterComponent } from '../correction-line-filter/correction-line-filter.component';
import { CorrectionNotesDialogComponent } from '../correction-notes-dialog/correction-notes-dialog.component';
import {
  CORRECTION_LINE_COLUMNS,
  groupLineIdsByDataType,
  notifyCorrectionResults,
  toCorrectionLine,
} from '../correction-lines';

/** Adjusted (AD) / voided (VD) lines whose adjustment can be reverted. */
@Component({
  selector: 'ppl-revert-adjustment',
  standalone: true,
  imports: [CommonModule, ListTableComponent, CorrectionLineFilterComponent, CorrectionNotesDialogComponent],
  templateUrl: './revert-adjustment.component.html',
  styleUrl: './revert-adjustment.component.css'
})
export class RevertAdjustmentComponent implements OnInit {
  pageId: string = PAGE_CONSTANTS.STAFF_REVERT_ADJUSTMENT;
  private readonly REVERT_BUTTON_ID = 'Revert';
  private readonly PAGE_NAME = 'payrollRevertAdjustmentList';

  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;

  actionButtons: TableRowActionsModel = new TableRowActions({ view: false });
  programCode = '';
  lines: any[] = [];
  hasSearched = false;
  isReadOnly = true;
  showRevertPopup = false;
  selectedLines: any[] = [];
  cols = CORRECTION_LINE_COLUMNS;
  private appliedFilters: any[] = [];

  buttons = [
    {
      button_id: this.REVERT_BUTTON_ID,
      button_label: 'Revert',
      buttonAction: 'revertAdjustment',
      className: 'primary-btn-color'
    },
  ];

  constructor(
    private payrollCorrectionService: PayrollCorrectionService,
    private notificationService: NotificationService,
    private userService: UserService,
    private allowListStore: AllowListAuthStore
  ) { }

  ngOnInit(): void {
    this.programCode = this.userService.getSelectedProgram()?.program_code ?? '';
    this.isReadOnly = !this.allowListStore.isButtonAllowed(this.pageId, this.REVERT_BUTTON_ID);
  }

  /** Nothing is loaded until the user searches; the full list grows too large to load by default. */
  onSearch(filters: any[]) {
    this.appliedFilters = filters;
    this.hasSearched = true;
    this.reload();
  }

  onClear() {
    this.appliedFilters = [];
    this.hasSearched = false;
    this.lines = [];
  }

  onPageLoad(event: any) {
    this.loadLines(event.startingPageNumber, event.rows ?? 20);
  }

  loadLines(startingPageNumber = 1, recordsPerPage = 20) {
    const payload = {
      pageName: this.PAGE_NAME,
      programCode: this.programCode,
      condition: 'and',
      recordsPerPage,
      startingPageNumber,
      searchParam: this.appliedFilters,
      orderByColumn: ['providerId'],
      orderBy: ['desc'],
    };

    this.payrollCorrectionService.getListPageData(payload).subscribe((data: any) => {
      const records = (data?.responsedata ?? []).map(toCorrectionLine);
      this.lines = startingPageNumber > 1 ? [...this.lines, ...records] : records;
    });
  }

  gridAction(event: any) {
    if (event.button.buttonAction !== 'revertAdjustment') return;
    this.selectedLines = event.selectedCheckBox ?? [];
    this.showRevertPopup = true;
  }

  submitRevert(notes: string) {
    const requests = Array.from(groupLineIdsByDataType(this.selectedLines), ([dataType, ids]) =>
      this.payrollCorrectionService.revertAdjustment(this.programCode, dataType, notes, ids)
    );
    forkJoin(requests).subscribe((responses: any[]) => {
      this.showRevertPopup = false;
      notifyCorrectionResults(this.notificationService, responses.flatMap(correctionResults), 'reverted');
      this.reload();
    });
  }

  /** Clear the grid's selection so reverted lines aren't sent again, then reload from page 1. */
  private reload() {
    this.selectedLines = [];
    if (this.childComponent?.selectedCheckBoxValue) {
      this.childComponent.selectedCheckBoxValue = [];
    }
    this.loadLines(1, this.childComponent?.recordsPerPage ?? 20);
  }
}
